/*
 * Laufzeit-Konstanten und Helfer OHNE zod-Abhängigkeit.
 * Der Renderer importiert nur diese – so landet zod nicht im Formular-Bundle.
 */
import type { Block, ContentBlock, Field, FieldType } from "./fields";
import type { ComparisonOperator } from "./logic";

/** Sentinel-Choice-ID für „Sonstiges“. Der Freitext liegt in `Submission.other[fieldId]`. */
export const OTHER_CHOICE_ID = "__other__";

export const SCHEMA_VERSION = 1 as const;

export const CONTENT_BLOCK_TYPES: ReadonlySet<string> = new Set(["heading", "paragraph", "image", "video", "banner", "divider", "document"]);

/** Feldtypen, deren Antwort Choice-IDs enthält. */
/** Feldtypen innerhalb einer wiederholbaren Gruppe (muss zu `GroupSubField` passen). */
export const GROUP_SUBFIELD_TYPES = ["short_text", "long_text", "email", "phone", "url", "number", "date", "time", "datetime", "single_choice", "multiple_choice", "dropdown", "yes_no", "rating"] as const;

export const CHOICE_FIELD_TYPES: ReadonlySet<FieldType> = new Set<FieldType>(["single_choice", "multiple_choice", "dropdown", "ranking"]);

export const UNARY_OPERATORS: ReadonlySet<ComparisonOperator> = new Set<ComparisonOperator>(["is_empty", "is_not_empty"]);

export function isField(block: Block): block is Field {
  return !CONTENT_BLOCK_TYPES.has(block.type);
}

export function isContentBlock(block: Block): block is ContentBlock {
  return CONTENT_BLOCK_TYPES.has(block.type);
}

/* ---------- Trennlinie ---------- */

/** Symbole für die Trennlinie. Pfade liegen im Renderer/Builder (Lucide). */
export const DIVIDER_ICON_NAMES = ["star", "sparkles", "info", "alert", "check", "lightbulb", "heart", "user", "users", "mail", "calendar", "clock", "pin", "payment", "shield", "document", "leaf", "flask", "rocket", "flag"] as const;
export type DividerIcon = (typeof DIVIDER_ICON_NAMES)[number];
/** Beschriftungen im Builder. */
export const DIVIDER_ICON_LABELS: Record<DividerIcon, string> = {
  star: "Stern",
  sparkles: "Highlight",
  info: "Info",
  alert: "Achtung",
  check: "Erledigt",
  lightbulb: "Tipp",
  heart: "Herz",
  user: "Person",
  users: "Gruppe",
  mail: "Kontakt",
  calendar: "Termin",
  clock: "Zeit",
  pin: "Ort",
  payment: "Zahlung",
  shield: "Datenschutz",
  document: "Dokument",
  leaf: "Natur",
  flask: "Experiment",
  rocket: "Start",
  flag: "Ziel",
};

/** Ausprägung 1–5 → Linienstärke (px) und Deckkraft. Neutral ist auf Stufe 2 so dezent wie bisher. */
export function dividerLook(strength: number, neutral: boolean): { px: number; opacity: number } {
  const i = Math.min(5, Math.max(1, Math.round(strength || 2))) - 1;
  return { px: [1, 1, 2, 3, 4][i]!, opacity: (neutral ? [0.1, 0.18, 0.35, 0.6, 0.9] : [0.3, 0.55, 0.8, 1, 1])[i]! };
}
